import React, { useState, useEffect, useCallback, useRef } from 'react';
import styled from 'styled-components';
import { useNavigate, useLocation } from 'react-router-dom';
import { FiX, FiSearch, FiPaperclip, FiFile } from 'react-icons/fi';
import { searchActiveUser } from '../api/UserAuthApi';
import { sendDirectMessage, sendBrMessage, uploadMessageFileToTempFile } from '../api/NbmessageApi';
import { getAccessToken, useUserInfoQuery } from '../react-queries/queries';
import { processApiResponse } from '../common/errorMessage';
import { NoobookRole } from '../common/enums';
import LoadingOverlay from '../components/LoadingOverlay';
import MessageModal from '../components/MessageModal';
import { useNoobookContext } from '../context/NoobookContext';

const Wrapper = styled.div`
  max-width: 480px;
  margin: 0 auto;
  background: #fff;
  min-height: 100vh;
`;

const Content = styled.div`
  padding: 20px 20px 80px;
`;

const Section = styled.div`
  margin-bottom: 20px;
`;

const SectionLabel = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #555;
  margin-bottom: 8px;
`;

const BroadcastCheckboxRow = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
  font-size: 14px;
  color: #222;
  cursor: pointer;
  user-select: none;

  input {
    width: 16px;
    height: 16px;
    cursor: pointer;
    accent-color: #244AC2;
  }
`;

const SearchRow = styled.div`
  display: flex;
  gap: 8px;
`;

const SearchInput = styled.input`
  flex: 1;
  border: 1px solid #ddd;
  border-radius: 8px;
  padding: 10px 12px;
  font-size: 14px;
  outline: none;

  &:focus {
    border-color: #244AC2;
  }

  &::placeholder {
    color: #999;
  }

  &:disabled {
    background: #f5f5f5;
    color: #999;
    cursor: not-allowed;
  }
`;

const SearchButton = styled.button`
  border: none;
  border-radius: 8px;
  background: #244AC2;
  color: #fff;
  padding: 0 14px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  &:disabled {
    background: #ccc;
    cursor: not-allowed;
  }
`;

const SelectedRecipient = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 10px 12px;
  border: 1px solid #244AC2;
  border-radius: 8px;
  background: #f8fbff;
`;

const SelectedRecipientInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const SelectedRecipientName = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #222;
`;

const SelectedRecipientLoginId = styled.div`
  font-size: 12px;
  color: #888;
`;

const ClearRecipientButton = styled.button`
  background: none;
  border: none;
  color: #999;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    color: #e74c3c;
  }
`;

const SearchResultList = styled.div`
  margin-top: 8px;
  border: 1px solid #eee;
  border-radius: 8px;
  overflow: hidden;
  max-height: 240px;
  overflow-y: auto;
`;

const SearchResultItem = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  border: none;
  border-bottom: 1px solid #f0f0f0;
  background: #fff;
  cursor: pointer;
  text-align: left;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: #f8f9fa;
  }
`;

const UserAvatar = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: #eaeaea;
  overflow: hidden;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #bbb;
  font-size: 14px;
`;

const UserAvatarImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const UserInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const UserName = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: #222;
`;

const UserLoginId = styled.div`
  font-size: 12px;
  color: #888;
`;

const EmptyResult = styled.div`
  padding: 16px;
  text-align: center;
  color: #999;
  font-size: 13px;
`;

const TitleInput = styled.input`
  width: 100%;
  box-sizing: border-box;
  border: 1px solid #ddd;
  border-radius: 8px;
  padding: 10px 12px;
  font-size: 14px;
  outline: none;

  &:focus {
    border-color: #244AC2;
  }

  &::placeholder {
    color: #999;
  }
`;

const MessageTextareaContainer = styled.div`
  position: relative;
`;

const MessageTextarea = styled.textarea`
  width: 100%;
  box-sizing: border-box;
  min-height: 180px;
  border: 1px solid #ddd;
  border-radius: 8px;
  padding: 12px;
  padding-bottom: 48px;
  font-size: 14px;
  font-family: inherit;
  resize: vertical;
  outline: none;
  line-height: 1.5;

  &:focus {
    border-color: #244AC2;
  }

  &::placeholder {
    color: #999;
  }
`;

const AttachButton = styled.button`
  position: absolute;
  bottom: 12px;
  right: 12px;
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 50%;
  background-color: #244AC2;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const PendingFileList = styled.div`
  display: flex;
  gap: 8px;
  overflow-x: auto;
  margin-top: 8px;
  padding: 4px 0;

  &::-webkit-scrollbar {
    height: 4px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 2px;
  }

  &::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 2px;
  }
`;

const PendingFileItem = styled.div`
  position: relative;
  flex-shrink: 0;
  width: 48px;
  height: 48px;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #e0e0e0;
  background: #fafafa;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #666;
`;

const PendingFileThumbnail = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const PendingFileName = styled.span`
  font-size: 7px;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-align: center;
  padding: 0 2px;
`;

const RemoveFileButton = styled.button`
  position: absolute;
  top: 2px;
  right: 2px;
  width: 18px;
  height: 18px;
  border: none;
  border-radius: 50%;
  background-color: rgba(0, 0, 0, 0.6);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
`;

const HiddenFileInput = styled.input`
  display: none;
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 24px;
`;

const ActionButton = styled.button`
  flex: 1;
  border: none;
  border-radius: 8px;
  padding: 12px 16px;
  font-size: 15px;
  font-weight: 500;
  cursor: pointer;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const CancelButton = styled(ActionButton)`
  background: #f5f5f5;
  color: #666;
`;

const SendButton = styled(ActionButton)`
  background: #244AC2;
  color: #fff;
`;

const USER_SEARCH_SIZE = 20;
const MAX_ATTACH_FILES = 10;

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

export default function MessageWritePage({ recipientLoginId: propRecipientLoginId = null, title: propTitle = null } = {}) {
  const navigate = useNavigate();
  const location = useLocation();
  const query = useQuery();
  const { showBack, setShowBack, logoText, setLogoText } = useNoobookContext();
  const { data: userInfo } = useUserInfoQuery();

  const recipientLoginId =
    propRecipientLoginId ||
    location.state?.recipientLoginId ||
    query.get('recipientLoginId') ||
    '';
  const initialTitle =
    propTitle ||
    location.state?.title ||
    query.get('title') ||
    '';

  const isRecipientLocked = !!recipientLoginId;

  const [recipientKeyword, setRecipientKeyword] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [selectedRecipient, setSelectedRecipient] = useState(null);
  const [title, setTitle] = useState(initialTitle);
  const [msgText, setMsgText] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [sendToAll, setSendToAll] = useState(false);

  const fileInputRef = useRef(null);
  const isAdmin = userInfo?.userRole === NoobookRole.ADMIN && !isRecipientLocked;

  useEffect(() => {
    var prevShowBack = showBack;
    var prevLogoText = logoText;
    setShowBack(true);
    setLogoText('메시지 작성');
    return () => {
      setShowBack(prevShowBack);
      setLogoText(prevLogoText);
    };
  }, [setShowBack, setLogoText]);

  useEffect(() => {
    if (initialTitle) {
      setTitle(initialTitle);
    }
  }, [initialTitle]);

  useEffect(() => {
    if (isRecipientLocked) {
      return;
    }

    const toUserId = query.get('toUserId');
    const toUserName = query.get('toUserName');
    const toUserLoginid = query.get('toUserLoginid');

    if (toUserId) {
      setSelectedRecipient({
        id: Number(toUserId),
        name: toUserName || '',
        loginid: toUserLoginid || '',
        imgFileUrl: query.get('toUserImg') || '',
      });
    }
  }, [query, isRecipientLocked]);

  useEffect(() => {
    if (!recipientLoginId) {
      return;
    }

    let cancelled = false;

    const loadRecipientByLoginId = async () => {
      setIsSearching(true);
      setErrorMessage('');
      setSendToAll(false);

      try {
        const { accessToken } = await getAccessToken();
        if (!accessToken) {
          if (!cancelled) {
            setErrorMessage('인증 토큰을 가져올 수 없습니다.');
          }
          return;
        }

        const response = await searchActiveUser(
          accessToken,
          recipientLoginId.trim(),
          0,
          USER_SEARCH_SIZE,
        );
        const processedResponse = processApiResponse(response);

        if (cancelled) {
          return;
        }

        if (processedResponse.success && Array.isArray(processedResponse.data) && processedResponse.data.length > 0) {
          setSelectedRecipient(processedResponse.data[0]);
        } else {
          setSelectedRecipient(null);
          setErrorMessage(processedResponse.errorMessage || '수신자를 찾을 수 없습니다.');
        }
      } catch (err) {
        console.error('수신자 검색 오류:', err);
        if (!cancelled) {
          setSelectedRecipient(null);
          setErrorMessage(err.message || '수신자 검색 중 오류가 발생했습니다.');
        }
      } finally {
        if (!cancelled) {
          setIsSearching(false);
        }
      }
    };

    loadRecipientByLoginId();

    return () => {
      cancelled = true;
    };
  }, [recipientLoginId]);

  const handleSearchRecipient = async () => {
    const keyword = recipientKeyword.trim();
    if (!keyword) {
      setErrorMessage('수신자 검색어를 입력해주세요.');
      return;
    }

    setIsSearching(true);
    setHasSearched(true);
    setErrorMessage('');

    try {
      const { accessToken } = await getAccessToken();
      if (!accessToken) {
        setErrorMessage('인증 토큰을 가져올 수 없습니다.');
        return;
      }

      const response = await searchActiveUser(accessToken, keyword, 0, USER_SEARCH_SIZE);
      const processedResponse = processApiResponse(response);

      if (processedResponse.success && Array.isArray(processedResponse.data)) {
        setSearchResults(processedResponse.data);
      } else {
        setSearchResults([]);
        setErrorMessage(processedResponse.errorMessage || '수신자 검색에 실패했습니다.');
      }
    } catch (err) {
      console.error('수신자 검색 오류:', err);
      setSearchResults([]);
      setErrorMessage(err.message || '수신자 검색 중 오류가 발생했습니다.');
    } finally {
      setIsSearching(false);
    }
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSearchRecipient();
    }
  };

  const handleSelectRecipient = (user) => {
    setSendToAll(false);
    setSelectedRecipient(user);
    setSearchResults([]);
    setHasSearched(false);
    setRecipientKeyword('');
  };

  const handleClearRecipient = () => {
    setSelectedRecipient(null);
    setSendToAll(false);
  };

  const handleBroadcastToggle = (e) => {
    const checked = e.target.checked;
    setSendToAll(checked);
    if (checked) {
      setSelectedRecipient(null);
      setSearchResults([]);
      setHasSearched(false);
      setRecipientKeyword('');
    }
  };

  const handleCancel = () => {
    navigate('/messages');
  };

  const handleFileSelect = useCallback((event) => {
    const files = Array.from(event.target.files);
    if ((selectedFiles.length + files.length) > MAX_ATTACH_FILES) {
      setErrorMessage(`첨부 파일은 최대 ${MAX_ATTACH_FILES}개까지 가능합니다.`);
      return;
    }

    files.forEach((file) => {
      const fileItem = {
        file,
        preview: null,
        id: Date.now() + Math.random(),
        fileName: file.name,
        fileExt: file.name.includes('.') ? file.name.split('.').pop() : '',
      };

      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (e) => {
          setSelectedFiles(prev => [...prev, { ...fileItem, preview: e.target.result }]);
        };
        reader.readAsDataURL(file);
      } else {
        setSelectedFiles(prev => [...prev, fileItem]);
      }
    });

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [selectedFiles]);

  const handleAttachClick = () => {
    fileInputRef.current?.click();
  };

  const handleRemoveFile = (fileId) => {
    setSelectedFiles(prev => prev.filter(file => file.id !== fileId));
  };

  const handleSend = async () => {
    if (!sendToAll && !selectedRecipient?.id) {
      setErrorMessage('수신자를 선택해주세요.');
      return;
    }
    if (!msgText.trim() && selectedFiles.length === 0) {
      setErrorMessage('메시지 내용 또는 첨부 파일을 입력해주세요.');
      return;
    }
    if (isSending) {
      return;
    }

    setIsSending(true);
    setErrorMessage('');

    try {
      const { accessToken } = await getAccessToken();
      if (!accessToken) {
        setErrorMessage('인증 토큰을 가져올 수 없습니다.');
        return;
      }

      const fileDTOs = [];
      for (const fileItem of selectedFiles) {
        const response = await uploadMessageFileToTempFile(accessToken, fileItem.file);
        const processedResponse = processApiResponse(response);
        if (processedResponse.success && processedResponse.data) {
          fileDTOs.push({
            id: null,
            fileStoreId: processedResponse.data.fileId,
            fileName: processedResponse.data.fileNm,
            fileExt: processedResponse.data.ext,
            fileUrl: processedResponse.data.fileUrl,
            fileNo: null,
          });
        } else {
          setErrorMessage(processedResponse.errorMessage || '파일 업로드에 실패했습니다.');
          return;
        }
      }

      const reqDto = {
        toUserId: sendToAll ? 0 : selectedRecipient.id,
        title: title.trim() || null,
        msgText: msgText.trim() || null,
        files: fileDTOs.length > 0 ? fileDTOs : null,
      };

      const response = sendToAll
        ? await sendBrMessage(accessToken, reqDto)
        : await sendDirectMessage(accessToken, reqDto);
      const processedResponse = processApiResponse(response);

      if (processedResponse.success) {
        navigate('/messages');
      } else {
        setErrorMessage(processedResponse.errorMessage || '메시지 전송에 실패했습니다.');
      }
    } catch (err) {
      console.error('메시지 전송 오류:', err);
      setErrorMessage(err.message || '메시지 전송 중 오류가 발생했습니다.');
    } finally {
      setIsSending(false);
    }
  };

  const renderUserAvatar = (user) => {
    if (user.imgFileUrl) {
      return <UserAvatarImage src={user.imgFileUrl} alt={user.name || user.loginid} />;
    }
    return (user.name || user.loginid || '?').charAt(0);
  };

  return (
    <Wrapper>
      <Content>
        <Section>
          <SectionLabel>받는 사람</SectionLabel>
          {isAdmin && !isRecipientLocked && (
            <BroadcastCheckboxRow>
              <input
                type="checkbox"
                checked={sendToAll}
                onChange={handleBroadcastToggle}
                disabled={isSending}
              />
              전체
            </BroadcastCheckboxRow>
          )}
          {sendToAll ? (
            <SelectedRecipient>
              <SelectedRecipientInfo>
                <SelectedRecipientName>전체</SelectedRecipientName>
                <SelectedRecipientLoginId>모든 사용자에게 전송</SelectedRecipientLoginId>
              </SelectedRecipientInfo>
              <ClearRecipientButton onClick={handleClearRecipient} aria-label="전체 선택 해제">
                <FiX size={16} />
              </ClearRecipientButton>
            </SelectedRecipient>
          ) : selectedRecipient ? (
            <SelectedRecipient>
              <SelectedRecipientInfo>
                <SelectedRecipientName>
                  {selectedRecipient.name || selectedRecipient.loginid}
                </SelectedRecipientName>
                {selectedRecipient.loginid && (
                  <SelectedRecipientLoginId>{selectedRecipient.loginid}</SelectedRecipientLoginId>
                )}
              </SelectedRecipientInfo>
              {!isRecipientLocked && (
                <ClearRecipientButton onClick={handleClearRecipient} aria-label="수신자 삭제">
                  <FiX size={16} />
                </ClearRecipientButton>
              )}
            </SelectedRecipient>
          ) : isRecipientLocked ? (
            <EmptyResult>{isSearching ? '수신자 조회 중...' : '수신자 정보 없음'}</EmptyResult>
          ) : (
            <>
              <SearchRow>
                <SearchInput
                  type="text"
                  placeholder="이름 또는 아이디로 검색"
                  value={recipientKeyword}
                  onChange={(e) => setRecipientKeyword(e.target.value)}
                  onKeyDown={handleSearchKeyDown}
                  disabled={sendToAll || isSending}
                />
                <SearchButton onClick={handleSearchRecipient} disabled={isSearching || sendToAll || isSending}>
                  <FiSearch size={18} />
                </SearchButton>
              </SearchRow>

              {isSearching && (
                <EmptyResult>검색 중...</EmptyResult>
              )}

              {!isSearching && hasSearched && searchResults.length === 0 && (
                <EmptyResult>검색 결과가 없습니다.</EmptyResult>
              )}

              {!isSearching && searchResults.length > 0 && (
                <SearchResultList>
                  {searchResults.map((user) => (
                    <SearchResultItem
                      key={user.id}
                      type="button"
                      onClick={() => handleSelectRecipient(user)}
                    >
                      <UserAvatar>{renderUserAvatar(user)}</UserAvatar>
                      <UserInfo>
                        <UserName>
                          {user.name || user.loginid}
                          {userInfo?.id === user.id ? ' (나)' : ''}
                        </UserName>
                        <UserLoginId>{user.loginid}</UserLoginId>
                      </UserInfo>
                    </SearchResultItem>
                  ))}
                </SearchResultList>
              )}
            </>
          )}
        </Section>

        <Section>
          <SectionLabel>제목</SectionLabel>
          <TitleInput
            type="text"
            placeholder="제목을 입력하세요 (선택)"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
        </Section>

        <Section>
          <SectionLabel>내용</SectionLabel>
          <MessageTextareaContainer>
            <MessageTextarea
              placeholder="메시지 내용을 입력하세요"
              value={msgText}
              onChange={(e) => setMsgText(e.target.value)}
            />
            <AttachButton
              type="button"
              onClick={handleAttachClick}
              disabled={isSending || selectedFiles.length >= MAX_ATTACH_FILES}
              aria-label="파일 첨부"
            >
              <FiPaperclip size={18} />
            </AttachButton>
          </MessageTextareaContainer>
          {selectedFiles.length > 0 && (
            <PendingFileList>
              {selectedFiles.map((fileItem) => (
                <PendingFileItem key={fileItem.id} title={fileItem.fileName}>
                  {fileItem.preview ? (
                    <PendingFileThumbnail src={fileItem.preview} alt={fileItem.fileName} />
                  ) : (
                    <>
                      <FiFile size={18} />
                      <PendingFileName>{fileItem.fileName}</PendingFileName>
                    </>
                  )}
                  <RemoveFileButton
                    type="button"
                    onClick={() => handleRemoveFile(fileItem.id)}
                    aria-label="첨부 파일 삭제"
                  >
                    <FiX size={10} />
                  </RemoveFileButton>
                </PendingFileItem>
              ))}
            </PendingFileList>
          )}
          <HiddenFileInput
            ref={fileInputRef}
            type="file"
            multiple
            onChange={handleFileSelect}
          />
        </Section>

        <ButtonRow>
          <CancelButton onClick={handleCancel} disabled={isSending}>
            취소
          </CancelButton>
          <SendButton onClick={handleSend} disabled={isSending}>
            {isSending ? '전송 중...' : '보내기'}
          </SendButton>
        </ButtonRow>
      </Content>

      <MessageModal
        isOpen={!!errorMessage}
        message={errorMessage}
        onClose={() => setErrorMessage('')}
        title="알림"
      />

      {isSending && <LoadingOverlay />}
    </Wrapper>
  );
}
